'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { PartnershipConfig } from '@/features/code/partnership-config'

interface Remaining {
    days: number
    hours: number
    minutes: number
    seconds: number
    expired: boolean
}

function computeRemaining(endsAtMs: number): Remaining {
    const diff = endsAtMs - Date.now()

    if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }
    }

    const seconds = Math.floor(diff / 1000) % 60
    const minutes = Math.floor(diff / (1000 * 60)) % 60
    const hours = Math.floor(diff / (1000 * 60 * 60)) % 24
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    return { days, hours, minutes, seconds, expired: false }
}

function pad(value: number) {
    return value.toString().padStart(2, '0')
}

function CountdownCell({ value, unit }: { value: string; unit: string }) {
    return (
        <div className="flex flex-col items-center min-w-[64px] md:min-w-[72px] rounded-s bg-white/10 px-3 py-2">
            <span className="text-white text-2xl md:text-3xl font-semibold tabular-nums">
                {value}
            </span>
            <span className="text-white/60 text-[10px] md:text-xs uppercase tracking-wide mt-1">
                {unit}
            </span>
        </div>
    )
}

export function PartnershipCountdown({ partnership }: { partnership: PartnershipConfig }) {
    const t = useTranslations()
    const endsAt = partnership.countdownEndsAt
    const endsAtMs = endsAt ? new Date(endsAt).getTime() : 0
    const [remaining, setRemaining] = useState<Remaining>(() => computeRemaining(endsAtMs))

    useEffect(() => {
        if (!endsAtMs) return

        const id = setInterval(() => {
            setRemaining(computeRemaining(endsAtMs))
        }, 1000)

        return () => clearInterval(id)
    }, [endsAtMs])

    if (!endsAt || remaining.expired) return null

    return (
        <div className="flex flex-col items-center w-full mb-12">
            <p className="text-white/70 text-sm md:text-base mb-4 text-center">
                {t('baseLayout.code.countdown.label')}
            </p>

            <div className="flex flex-row items-center gap-2 md:gap-3">
                <CountdownCell
                    value={pad(remaining.days)}
                    unit={t('baseLayout.code.countdown.days')}
                />
                <CountdownCell
                    value={pad(remaining.hours)}
                    unit={t('baseLayout.code.countdown.hours')}
                />
                <CountdownCell
                    value={pad(remaining.minutes)}
                    unit={t('baseLayout.code.countdown.minutes')}
                />
                <CountdownCell
                    value={pad(remaining.seconds)}
                    unit={t('baseLayout.code.countdown.seconds')}
                />
            </div>

            <div className="w-full h-px bg-white/20 mt-12" />
        </div>
    )
}
